import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Container,
	Tabs,
	Tab,
	Card,
	CardContent,
	Button,
	Typography,
	Breadcrumbs,
	Link,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { Customer } from '../../gql/graphql';
import Loader from '../../components/generic/Loader';
import { ADD_UPDATE_CUSTOMER, GET_CUSTOMER_BY_ID } from '../../gql/queries';
import { useShopDetails } from '../../hooks/use-shop';

// Tab Components
import BasicInfoTab from '../../components/customer/BasicInfoTab';
import OrderHistoryTab from '../../components/customer/OrderHistoryTab';
import AdvancedTab from '../../components/customer/AdvancedTab';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`customer-tabpanel-${index}`}
			aria-labelledby={`customer-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
		</div>
	);
}

function CustomerDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { shopId, locationId } = useShopDetails();
	const [currentTab, setCurrentTab] = useState(0);
	const [customerData, setCustomerData] = useState<Partial<Customer>>({});
	const [isLoading, setIsLoading] = useState(false);

	const isEditMode = !!id && id !== 'new';

	const [getCustomerById] = useLazyQuery<Record<'customer', Customer>>(
		GET_CUSTOMER_BY_ID
	);

	const [addUpdateCustomer, { loading: saving }] = useMutation(ADD_UPDATE_CUSTOMER, {
		onCompleted: (data) => {
			toast.success(
				`Customer: ${data.addUpdateCustomer.firstName} ${data.addUpdateCustomer.lastName} ${isEditMode ? 'updated' : 'created'} successfully.`
			);
			if (!isEditMode) {
				navigate(`/dashboard/customers/${data.addUpdateCustomer._id}/edit`);
			}
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	useEffect(() => {
		if (isEditMode) {
			loadCustomer();
		}
	}, [id]);

	const loadCustomer = async () => {
		setIsLoading(true);
		try {
			const { data } = await getCustomerById({
				variables: { id: id!, shopId, locationId },
			});
			if (data?.customer) {
				setCustomerData(data.customer);
			}
		} catch (error: any) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleSave = async () => {
		try {
			// Validate required fields
			if (!customerData.firstName || !customerData.phone) {
				toast.error('Please fill in all required fields (First Name, Phone)');
				return;
			}

			// Validate phone number
			if (customerData.phone && (customerData.phone.length < 10 || customerData.phone.length > 10)) {
				toast.error('Phone number must be exactly 10 digits');
				return;
			}

			await addUpdateCustomer({
				variables: { customer: { ...customerData, shopId, locationId } },
			});
		} catch (error: any) {
			toast.error(error?.message);
		}
	};

	const updateCustomerData = (updates: Partial<Customer>) => {
		setCustomerData((prev) => ({ ...prev, ...updates }));
	};

	return (
		<>
			<Loader isLoading={isLoading || saving} />
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 4,
				}}
			>
				<Container maxWidth="xl">
					{/* Header */}
					<Box sx={{ mb: 3 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
							<Button
								startIcon={<ArrowBack />}
								onClick={() => navigate('/dashboard/customers')}
								sx={{ mr: 2 }}
							>
								Back
							</Button>
							<Box sx={{ flexGrow: 1 }}>
								<Typography variant="h4">
									{isEditMode ? 'Edit Customer' : 'Create New Customer'}
								</Typography>
								<Breadcrumbs sx={{ mt: 1 }}>
									<Link
										underline="hover"
										color="inherit"
										onClick={() => navigate('/dashboard/customers')}
										sx={{ cursor: 'pointer' }}
									>
										Customers
									</Link>
									<Typography color="text.primary">
										{isEditMode ? `${customerData.firstName || ''} ${customerData.lastName || ''}`.trim() || 'Edit' : 'New Customer'}
									</Typography>
								</Breadcrumbs>
							</Box>
							<Button
								variant="contained"
								startIcon={<Save />}
								onClick={handleSave}
								disabled={saving}
							>
								Save Customer
							</Button>
						</Box>
					</Box>

					{/* Tabs */}
					<Card>
						<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
							<Tabs
								value={currentTab}
								onChange={handleTabChange}
								variant="scrollable"
								scrollButtons="auto"
							>
								<Tab label="Basic Information" />
								<Tab label="Order History" disabled={!isEditMode} />
								<Tab label="Advanced" />
							</Tabs>
						</Box>

						<CardContent>
							<TabPanel value={currentTab} index={0}>
								<BasicInfoTab
									customerData={customerData}
									updateCustomerData={updateCustomerData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={1}>
								{isEditMode && customerData._id && (
									<OrderHistoryTab customerId={customerData._id} />
								)}
							</TabPanel>

							<TabPanel value={currentTab} index={2}>
								<AdvancedTab
									customerData={customerData}
									updateCustomerData={updateCustomerData}
								/>
							</TabPanel>
						</CardContent>
					</Card>
				</Container>
			</Box>
		</>
	);
}

export default CustomerDetailPage;
